import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type CSSProperties,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react';
import type { AutocompleteCard } from '@/lib/api';

// ── Types ────────────────────────────────────────────────────────────────────

export interface CardAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  /** Fires with the full suggestion when one is picked (click, Enter, Tab), and when the typed text
   *  exactly matches a fetched suggestion (case-insensitive). `onChange` still fires as before. */
  onSelect?: (card: AutocompleteCard) => void;
  placeholder?: string;
  triggerMode?: '[[' | null;
  /** When true, pressing Tab on a non-empty word opens autocomplete (or completes immediately). */
  tabComplete?: boolean;
  style?: CSSProperties;
  /** If provided, applied as className on the <input>/<textarea>; default inline styles are suppressed. */
  inputClassName?: string;
  /** Extra classes added to the outer wrapper div. */
  wrapperClassName?: string;
  multiline?: boolean;
  rows?: number;
  colorIdentity?: string[];
  /** Only cards that can be a commander (legendary creatures, "can be your commander"). */
  commanderOnly?: boolean;
  /** Extra keydown handler called after the autocomplete handler (only when autocomplete didn't handle the key). */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /** Focus the input on mount. */
  autoFocus?: boolean;
  /** External ref forwarded to the underlying <input> element. */
  inputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
}

// ── Fetch cache ──────────────────────────────────────────────────────────────

const fetchCache = new Map<string, AutocompleteCard[]>();

async function fetchSuggestions(q: string, limit: number, ci?: string, commanderOnly = false): Promise<AutocompleteCard[]> {
  if (q.length < 2) return [];
  const key = `${q}|${limit}|${ci ?? ''}|${commanderOnly ? 'cmd' : ''}`;
  if (fetchCache.has(key)) return fetchCache.get(key)!;

  try {
    const params = new URLSearchParams({ q, limit: String(limit) });
    if (ci) params.set('ci', ci);
    if (commanderOnly) params.set('commander_only', 'true');
    const resp = await fetch(`/api/v1/cards/autocomplete?${params}`, {
      credentials: 'same-origin',
    });
    if (!resp.ok) return [];
    const data = await resp.json();
    const results: AutocompleteCard[] = data.cards || [];
    if (results.length > 0) {
      fetchCache.set(key, results);
      if (fetchCache.size > 200) {
        const first = fetchCache.keys().next().value;
        if (first !== undefined) fetchCache.delete(first);
      }
    }
    return results;
  } catch {
    return [];
  }
}

// ── Component ────────────────────────────────────────────────────────────────

export function CardAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  triggerMode = null,
  tabComplete = false,
  style,
  inputClassName,
  wrapperClassName,
  multiline = false,
  rows = 4,
  colorIdentity,
  commanderOnly = false,
  onKeyDown: onKeyDownProp,
  autoFocus = false,
  inputRef: externalInputRef,
}: CardAutocompleteProps) {
  const [results, setResults] = useState<AutocompleteCard[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [showDropdown, setShowDropdown] = useState(false);

  const internalInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const inputRef = externalInputRef ?? internalInputRef;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQueryRef = useRef('');
  // Word range captured during TAB-triggered completion (no [[ in text)
  const tabWordRangeRef = useRef<{ start: number; end: number } | null>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Position dropdown ──────────────────────────────────────────────────

  const [dropdownPos, setDropdownPos] = useState<{
    left: number;
    top?: number;
    bottom?: number;
    width: number;
  }>({ left: 0, width: 220 });

  const positionDropdown = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropH = 280;
    const above = spaceBelow < dropH + 8 && rect.top > dropH + 8;

    if (above) {
      setDropdownPos({
        left: rect.left,
        bottom: window.innerHeight - rect.top,
        width: Math.max(rect.width, 220),
      });
    } else {
      setDropdownPos({
        left: rect.left,
        top: rect.bottom + 2,
        width: Math.max(rect.width, 220),
      });
    }
  }, []);

  // ── Query extraction ───────────────────────────────────────────────────

  const getQuery = useCallback((): string | null => {
    const el = inputRef.current;
    if (!el) return null;

    if (triggerMode === '[[') {
      const text = el.value;
      const cursor = el.selectionStart ?? text.length;
      const before = text.slice(0, cursor);
      const lastOpen = before.lastIndexOf('[[');
      if (lastOpen < 0) return null;
      const partial = before.slice(lastOpen + 2);
      if (partial.includes(']]')) return null;
      return partial;
    }

    return el.value.trim();
  }, [triggerMode]);

  // ── Selection ──────────────────────────────────────────────────────────

  const selectCard = useCallback(
    (card: AutocompleteCard) => {
      const { name } = card;
      const el = inputRef.current;
      if (!el) return;

      if (triggerMode === '[[') {
        const text = el.value;
        const cursor = el.selectionStart ?? text.length;
        const before = text.slice(0, cursor);
        const lastOpen = before.lastIndexOf('[[');

        if (lastOpen < 0) {
          // No [[ found — TAB-triggered completion: replace the word at cursor
          const range = tabWordRangeRef.current;
          if (range) {
            const newValue = text.slice(0, range.start) + name + text.slice(range.end);
            const newCursor = range.start + name.length;
            tabWordRangeRef.current = null;
            onChange(newValue);
            requestAnimationFrame(() => {
              el.setSelectionRange(newCursor, newCursor);
              el.focus();
            });
          } else {
            // Fallback: replace entire value
            onChange(name);
          }
        } else {
          const after = text.slice(cursor);
          const replacement = name;
          const newValue = text.slice(0, lastOpen) + replacement + after;
          const newCursor = lastOpen + replacement.length;
          onChange(newValue);
          // Set cursor position after React re-render
          requestAnimationFrame(() => {
            el.setSelectionRange(newCursor, newCursor);
            el.focus();
          });
        }
      } else {
        onChange(name);
      }

      onSelect?.(card);
      setShowDropdown(false);
      setActiveIndex(-1);
      setResults([]);
    },
    [triggerMode, onChange, onSelect],
  );

  // ── Fetch update ───────────────────────────────────────────────────────

  const doUpdate = useCallback(async () => {
    const q = getQuery();
    if (!q || q.length < 2) {
      setShowDropdown(false);
      return;
    }
    if (q === lastQueryRef.current) return;
    lastQueryRef.current = q;

    const ciParam = colorIdentity?.length ? colorIdentity.join('') : undefined;
    const data = await fetchSuggestions(q, 8, ciParam, commanderOnly);
    // Typed text that exactly names a suggestion (case-insensitive) resolves it, as a pick would, and
    // highlights it so Enter/Tab take it rather than the first suggestion. Re-read the query: the
    // fetch is async, so the text may have moved on while it was in flight.
    const now = getQuery()?.toLowerCase();
    const exactIdx = now ? data.findIndex((c) => c.name.toLowerCase() === now) : -1;
    setResults(data);
    setActiveIndex(exactIdx);
    if (exactIdx >= 0) onSelect?.(data[exactIdx]);

    if (data.length === 0) {
      setShowDropdown(false);
      return;
    }

    positionDropdown();
    setShowDropdown(true);
  }, [getQuery, positionDropdown, colorIdentity, commanderOnly, onSelect]);

  // ── Event handlers ─────────────────────────────────────────────────────

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(e.target.value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(doUpdate, 120);
    },
    [onChange, doUpdate],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // ── Dropdown open: navigation + selection ──────────────────────────
      if (showDropdown && results.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveIndex(prev => (prev + 1) % results.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveIndex(prev => (prev - 1 + results.length) % results.length);
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          e.stopPropagation();
          selectCard(results[activeIndex >= 0 ? activeIndex : 0]);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setShowDropdown(false);
          setActiveIndex(-1);
          tabWordRangeRef.current = null;
          return;
        }
      }

      // ── Tab-complete: open dropdown for current word ───────────────────
      // getQuery() returns null when triggerMode='[[' and no [[ is present,
      // so read the raw input value directly for TAB completion.
      if (e.key === 'Tab' && tabComplete && !showDropdown) {
        const el = inputRef.current;
        const rawText = el?.value ?? '';
        const cursor = el?.selectionStart ?? rawText.length;
        const before = rawText.slice(0, cursor);
        // Extract word at cursor (from last whitespace to cursor)
        const wordStart = Math.max(before.lastIndexOf(' '), before.lastIndexOf('\n')) + 1;
        const q = before.slice(wordStart).trim();
        if (q && q.length >= 2) {
          e.preventDefault();
          if (debounceRef.current) clearTimeout(debounceRef.current);
          // Store the word range so selectCard can replace it precisely
          tabWordRangeRef.current = { start: wordStart, end: cursor };
          void (async () => {
            const ciParam = colorIdentity?.length ? colorIdentity.join('') : undefined;
            const data = await fetchSuggestions(q, 8, ciParam, commanderOnly);
            if (data.length > 0) {
              setResults(data);
              setActiveIndex(0);
              positionDropdown();
              setShowDropdown(true);
            }
          })();
        }
      }
    },
    [showDropdown, results, activeIndex, selectCard, tabComplete, colorIdentity, commanderOnly, positionDropdown],
  );

  const handleBlur = useCallback(() => {
    // Delay so mousedown on dropdown fires first
    setTimeout(() => {
      setShowDropdown(false);
      tabWordRangeRef.current = null;
    }, 150);
  }, []);

  const handleFocus = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(doUpdate, 200);
  }, [doUpdate]);

  // Reposition on scroll
  useEffect(() => {
    if (!showDropdown) return;
    const onScroll = () => positionDropdown();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [showDropdown, positionDropdown]);

  // ── Render ─────────────────────────────────────────────────────────────

  // When inputClassName is provided, use it + any explicit style overrides.
  // Otherwise fall back to the default inline style block.
  const defaultStyle: CSSProperties = inputClassName ? { ...style } : {
    width: '100%',
    padding: '10px 14px',
    background: 'var(--surface-container-high)',
    border: '1px solid var(--outline-variant)',
    borderRadius: 'var(--radius-sm, 4px)',
    color: 'var(--on-surface)',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box' as const,
    ...style,
  };

  const composedKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleKeyDown(e);
      if (!e.defaultPrevented) onKeyDownProp?.(e);
    },
    [handleKeyDown, onKeyDownProp],
  );

  const inputProps = {
    ref: inputRef as React.RefObject<HTMLInputElement & HTMLTextAreaElement>,
    value,
    onChange: handleChange,
    onKeyDown: composedKeyDown,
    onBlur: handleBlur,
    onFocus: handleFocus,
    placeholder,
    className: inputClassName,
    style: defaultStyle,
  };

  return (
    <div className={`relative w-full${wrapperClassName ? ` ${wrapperClassName}` : ''}`}>
      {multiline ? (
        <textarea {...inputProps} rows={rows} style={{ ...defaultStyle, resize: 'vertical' }} />
      ) : (
        <input type="text" {...inputProps} />
      )}

      {showDropdown && results.length > 0 && (
        <div
          ref={dropdownRef}
          role="listbox"
          className="fixed bg-surface-container-high border border-outline-variant rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.4)] overflow-y-auto max-h-[280px] py-1 z-[9999]"
          style={{
            left: dropdownPos.left,
            top: dropdownPos.top,
            bottom: dropdownPos.bottom,
            width: dropdownPos.width,
          }}
        >
          {results.map((card, idx) => {
            const typeShort = (card.type_line || '').split('\u2014')[0].trim();
            return (
              <div
                key={card.name}
                role="option"
                aria-selected={idx === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectCard(card);
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`flex items-center gap-2 py-[7px] px-3 cursor-pointer text-base text-on-surface whitespace-nowrap overflow-hidden ${
                  idx === activeIndex ? 'bg-surface-container-highest' : ''
                }`}
              >
                <span className="font-medium shrink-0">{card.name}</span>
                <span className="text-on-surface-variant text-xs overflow-hidden text-ellipsis flex-1">
                  {typeShort}
                </span>
                {card.game_changer && (
                  <span className="text-xxs font-bold py-px px-1 rounded-[3px] bg-status-warning-bg text-warning-text shrink-0">
                    GC
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
